import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  selectEnvironmentInteractionPoints,
  TakePosition,
} from "../../app/slices/EnvironmentSlice";
import {
  selectExpandedTakeLocationIds,
  selectSequencePositionPicker,
  selectShowEnvironmentProps,
  selectSelectedTab,
  SelectedTab,
} from "../../app/slices/GlobalSlice";
import { getCollapsedTakeLocationIds } from "./takeLocationCollapse";
import {
  selectIsCameraSelected,
  selectSelectedElementId,
  selectSelectedTake3D,
} from "../../app/slices/TrainingSlice";
import "./Viewport3DEnvironmentMetadata.css";
import Viewport3DNpcLayer from "./Viewport3DNpcLayer";
import Viewport3DTakeLocationLayer from "./ViewportTakeLocationLayer";
import Viewport3DPropLayer from "./Viewport3DPropLayer";
import Viewport3DCameraLayer from "./Viewport3DCameraLayer";
import Viewport3DSituationLayer from "./situation_layer/Viewport3DSituationLayer";
import Viewport3DSequenceActorPositionLayer from "./Viewport3DSequenceActorPositionLayer";
import Viewport3DSequenceCameraPositionLayer from "./Viewport3DSequenceCameraPositionLayer";
import Viewport3DSequenceProximityPositionLayer from "./Viewport3DSequenceProximityPositionLayer";
import Viewport3DSequencePropPositionLayer from "./Viewport3DSequencePropPositionLayer";
import Viewport3DSequenceLookatTargetLayer from "./Viewport3DSequenceLookatTargetLayer";
import Viewport3DEnvironmentPropLayer from "./Viewport3DEnvironmentPropLayer";
import Viewport3DTakeLocationCollapseLayer from "./Viewport3DTakeLocationCollapseLayer";
import Viewport3DTakePropInspectLayer from "./Viewport3DTakePropInspectLayer";
import Viewport3DSequenceStateLayer from "./Viewport3DSequenceStateLayer";
import Viewport3DTakeAttachmentLayer from "./Viewport3DTakeAttachmentLayer";
import Viewport3DCameraPickingFocus from "./Viewport3DCameraPickingFocus";
import Viewport3DCameraPicking from "./Viewport3DCameraPicking";
import { filterTakePositionsByLayers } from "./environmentLayerVisibility";

export interface Viewport3DLayerProps {
  currentTakeLocation?: TakePosition;
  takePositions: TakePosition[];
  isCameraSelected?: boolean;
  /** Locations whose positions are folded into a single pin; their markers are skipped. */
  collapsedTakeLocationIds?: string[];
}

export default function EnvironmentMetadataView(props: {
  visibleLayerIds?: string[];
}) {
  const { visibleLayerIds = [] } = { ...props };
  const take = useSelector(selectSelectedTake3D);
  const takePositions = useSelector(selectEnvironmentInteractionPoints);
  const isTakeCameraSelected = useSelector(selectIsCameraSelected);
  const selectedElementId = useSelector(selectSelectedElementId);
  const selectedTab = useSelector(selectSelectedTab);
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);
  const showEnvironmentProps = useSelector(selectShowEnvironmentProps);
  const expandedTakeLocationIds = useSelector(selectExpandedTakeLocationIds);

  const currentTakeLocationAll = useMemo(() => {
    return take?.interactionPointId
      ? takePositions.find((point) => point.id === take.interactionPointId)
      : undefined;
  }, [take, takePositions]);

  const filteredTakePositions = useMemo(
    () =>
      filterTakePositionsByLayers(
        takePositions,
        visibleLayerIds,
        currentTakeLocationAll?.layer
      ),
    [takePositions, visibleLayerIds, currentTakeLocationAll?.layer]
  );

  const currentTakeLocation = useMemo(() => {
    return take?.interactionPointId
      ? filteredTakePositions.find((point) => point.id === take.interactionPointId)
      : undefined;
  }, [filteredTakePositions, take]);

  // Every location but the take's own is folded unless the user unfolded it.
  const collapsedTakeLocationIds = useMemo(
    () =>
      getCollapsedTakeLocationIds(
        filteredTakePositions,
        currentTakeLocation?.id,
        expandedTakeLocationIds ?? []
      ),
    [filteredTakePositions, currentTakeLocation?.id, expandedTakeLocationIds]
  );

  const isCameraPicking =
    isTakeCameraSelected && selectedTab === SelectedTab.Timeline;

  const isSequencePositionPicking =
    sequencePositionPicker.isOpen;

  if (isSequencePositionPicking) {
    if (sequencePositionPicker.positionType === "takeLocation") {
      return (
        <Viewport3DTakeLocationLayer
          currentTakeLocation={currentTakeLocation}
          takePositions={filteredTakePositions}
          isCameraSelected={isTakeCameraSelected}
          pickingMode
        />
      );
    }
    const pickerLayer =
      sequencePositionPicker.positionType === "lookatTarget" ? (
        <Viewport3DSequenceLookatTargetLayer />
      ) : sequencePositionPicker.positionType === "camera" ? (
        <Viewport3DSequenceCameraPositionLayer />
      ) : sequencePositionPicker.positionType === "prop" ? (
        <Viewport3DSequencePropPositionLayer />
      ) : sequencePositionPicker.positionType === "proximity" ? (
        <Viewport3DSequenceProximityPositionLayer />
      ) : (
        <Viewport3DSequenceActorPositionLayer />
      );
    return (
      <>
        {pickerLayer}
        {/* The camera picker badges the active camera on its own markers. */}
        {sequencePositionPicker.positionType !== "camera" && <Viewport3DSequenceStateLayer />}
      </>
    );
  }

  if (
    selectedTab === SelectedTab.Timeline &&
    isTakeCameraSelected &&
    take?.startViewMode === "propInspect" &&
    selectedElementId === take.mainCamera.id
  ) {
    return <Viewport3DTakePropInspectLayer />;
  }

  if (showEnvironmentProps) {
    return <Viewport3DEnvironmentPropLayer />;
  }

  return (
    <>
      <Viewport3DCameraPickingFocus active={isCameraPicking} />
      {isCameraPicking && <Viewport3DCameraPicking />}
      <Viewport3DNpcLayer
        currentTakeLocation={currentTakeLocation}
        takePositions={filteredTakePositions}
        isCameraSelected={isTakeCameraSelected}
        collapsedTakeLocationIds={collapsedTakeLocationIds}
      />
      <Viewport3DTakeLocationLayer
        currentTakeLocation={currentTakeLocation}
        takePositions={filteredTakePositions}
        isCameraSelected={isTakeCameraSelected}
      />
      <Viewport3DPropLayer
        currentTakeLocation={currentTakeLocation}
        takePositions={filteredTakePositions}
        isCameraSelected={isTakeCameraSelected}
        collapsedTakeLocationIds={collapsedTakeLocationIds}
      />
      <Viewport3DCameraLayer
        currentTakeLocation={currentTakeLocation}
        takePositions={filteredTakePositions}
        isCameraSelected={isTakeCameraSelected}
      />
      <Viewport3DSituationLayer
        currentTakeLocation={currentTakeLocation}
        takePositions={filteredTakePositions}
        isCameraSelected={isTakeCameraSelected}
        collapsedTakeLocationIds={collapsedTakeLocationIds}
      />
      <Viewport3DTakeLocationCollapseLayer
        currentTakeLocation={currentTakeLocation}
        takePositions={filteredTakePositions}
        isCameraSelected={isTakeCameraSelected}
        collapsedTakeLocationIds={collapsedTakeLocationIds}
      />
      <Viewport3DEnvironmentPropLayer />
      <Viewport3DSequenceActorPositionLayer />
      <Viewport3DTakeAttachmentLayer />
      <Viewport3DSequenceStateLayer />
    </>
  );
}
